import type { FocusTrap } from 'focus-trap';

import { createFocusTrap } from 'focus-trap';

export const useFocusTrap = (options = {}) => {
  const elementRef = ref<HTMLElement | null>(null);
  let focusTrap: FocusTrap | null = null;

  // The focus-trap library throws when the trapped subtree has no tabbable nodes — which can
  // happen during a transient render (e.g. SPA mount when `open` flips before children paint).
  // We swallow these errors so they don't abort the Vue watcher / component-update they're
  // running inside; losing focus-trap behaviour for a frame is preferable to a stuck DOM.
  const activate = () => {
    if (!elementRef.value) {
      return;
    }
    try {
      focusTrap = createFocusTrap(elementRef.value, {
        escapeDeactivates: false,
        allowOutsideClick: true,
        ...options,
      });
      focusTrap.activate();
    }
    catch {
      focusTrap = null;
    }
  };

  const deactivate = () => {
    if (!focusTrap) {
      return;
    }
    try {
      focusTrap.deactivate();
    }
    catch {
      // ignore — see comment above
    }
    finally {
      focusTrap = null;
    }
  };

  onMounted(() => {
    activate();
  });

  onBeforeUnmount(() => {
    deactivate();
  });

  return {
    elementRef,
    activate,
    deactivate,
  };
};
